import { getENV } from '@libs/common-next';
import { logSentryError } from '@libs/common-next/utils';
import type { StoreService } from '@libs/common-node';
import type { StoreV2 } from '@libs/common-types';
import { isLocaleCA, isValidUrl } from '@libs/core';

import { SentryErrors } from '@/shared/sentry';

type ServiceUrlFunctions = {
    PetsHotel: (locale: string, storeNumber: number) => string;
    Adoptions: () => string;
    Veterinary: (locale: string) => string;
};

const STORE_DOMAIN = {
    US: 'https://stores.petsmart.com',
    CA: 'https://stores.petsmart.ca',
};

export const VETERINARY = 'veterinary';

const SERVICE_URLS: ServiceUrlFunctions = {
    PetsHotel: (locale: string, storeNumber: number) => {
        const url =
            process.env.NEXT_PUBLIC_APP_ENV?.toLowerCase() === 'prod'
                ? `https://services.petsmart.${locale}/petshotel/${storeNumber}`
                : `https://services-staging.petsmart.${locale}/petshotel/${storeNumber}`;
        return url;
    },
    Adoptions: () => 'https://petsmartcharities.org/adopt-a-pet',
    Veterinary: (locale: string) => `https://www.petsmart.${locale}/pet-services/petsmart-veterinary-care.html`,
};

const SERVICE_PATHS: Record<string, string> = {
    Training: 'dog-training',
    Grooming: 'grooming',
    'Doggie Day Camp': 'petshotel-doggie-day-camp',
};

export const servicesToDisplay = ['Veterinary', 'PetsHotel', 'Grooming', 'Doggie Day Camp', 'Training', 'Adoptions'];

const makeSnakeCase = (str: string) =>
    str
        .replace(/^(N\.|S\.|E\.|W\.)\s*/i, '')
        .replace(/[()]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .toLowerCase();

function getVeterinaryCareUrl(locale: string, store: StoreV2, veterinaryCareUrl: string | undefined): string {
    if (store.CountryAbbreviation === 'US' && veterinaryCareUrl) {
        if (isValidUrl(veterinaryCareUrl)) {
            return veterinaryCareUrl;
        }

        logSentryError(veterinaryCareUrl, {
            ...SentryErrors.StoreDetailsServicesModule.InvalidVeterinaryCareUrl,
            extra: {
                ...SentryErrors.StoreDetailsServicesModule.InvalidVeterinaryCareUrl.extra,
                storeNumber: String(store.StoreNumber),
                invalidUrl: veterinaryCareUrl,
            },
        });
    }

    return SERVICE_URLS.Veterinary(locale);
}

export function constructServiceURL(service: StoreService, store: StoreV2): string | undefined {
    const isLocaleCa = isLocaleCA();
    const locale = isLocaleCa ? 'ca' : 'com';
    const domain = isLocaleCa ? STORE_DOMAIN.CA : STORE_DOMAIN.US;

    if (service.ServiceName in SERVICE_PATHS) {
        return `${domain}/${store.StateProvinceAbbreviation.toLowerCase()}/${makeSnakeCase(
            store.City
        )}/${makeSnakeCase(store.Name)}/${SERVICE_PATHS[service.ServiceName]}`;
    }

    if (service.ServiceName.toLowerCase() === VETERINARY) {
        return getVeterinaryCareUrl(locale, store, service.ServicePartnerLink);
    }

    return SERVICE_URLS[service.ServiceName as keyof ServiceUrlFunctions]?.(locale, store.StoreNumber);
}
const scene7StaticBase = getENV('NEXT_PUBLIC_SCENE7_STATIC_BASE');

export const serviceImages: Record<string, string> = {
    Grooming: `${scene7StaticBase}WEB-24-2287450-May24-StoreLoc-Grooming?fmt=webp&wid=60&hei=60`,
    Adoptions: `${scene7StaticBase}WEB-24-2287450-May24-StoreLoc-Adopt?fmt=webp&wid=60&hei=60`,
    Training: `${scene7StaticBase}WEB-24-2287450-May24-StoreLoc-Training?fmt=webp&wid=60&hei=60`,
    'PetSmart Veterinary Services': `${scene7StaticBase}Pet_Store_Locator_VIU_16TU_DT?fmt=webp&wid=60&hei=60`,
    Veterinary: `${scene7StaticBase}Pet_Store_Locator_VIU_16TU_DT?fmt=webp&wid=60&hei=60`,
    'Doggie Day Camp': `${scene7StaticBase}WEB-24-2287450-May24-StoreLoc-DDC?fmt=webp&wid=60&hei=60`,
    PetsHotel: `${scene7StaticBase}WEB-24-2287450-May24-StoreLoc-PetsHotel?fmt=webp&wid=60&hei=60`,
};
